// useChartWindow.js — 차트 윈도우 인터랙션 공통 훅
// 한 손가락 swipe → pan, 두 손가락 → pinch zoom, 마우스 drag → pan, 휠 → 부드러운 zoom (RAF ease-out)
import { useState, useEffect, useRef, useCallback } from 'react';

const MIN_WINDOW = 7;
const WHEEL_STEP = 1.10;
const TWEEN_DURATION = 220;
const PAN_THRESHOLD_PX = 4; // 이 거리를 넘으면 "pan으로 간주" → 종료 후에도 툴팁 숨김 유지

/**
 * @param {object} opts
 * @param {number} opts.dataLength  전체 데이터 길이
 * @param {number} opts.defaultWindow  isInteractive=true일 때 초기 윈도우 크기 (최신 N개)
 * @param {boolean} opts.isInteractive  인터랙션 활성 여부 (false면 전체 표시 + 핸들러 비활성)
 * @returns {{ viewWindow, chartRef, bindings }}
 */
export const useChartWindow = ({ dataLength, defaultWindow, isInteractive }) => {
  const [viewWindow, setViewWindow] = useState({ start: 0, end: 0 });
  const [isInteracting, setIsInteracting] = useState(false);
  const chartRef = useRef(null);
  const interactionRef = useRef(null);
  const tweenRafRef = useRef(0);
  const viewWindowRef = useRef(viewWindow);

  useEffect(() => { viewWindowRef.current = viewWindow; }, [viewWindow]);
  useEffect(() => () => cancelAnimationFrame(tweenRafRef.current), []);

  // 데이터가 비-인터랙티브로 바뀌면 sticky 상태 자동 해제
  useEffect(() => {
    if (!isInteractive) setIsInteracting(false);
  }, [isInteractive]);

  // 데이터/인터랙션 모드 변경 시 윈도우 초기화 — 최신 defaultWindow 구간 노출
  useEffect(() => {
    if (dataLength === 0) {
      setViewWindow({ start: 0, end: 0 });
      return;
    }
    if (!isInteractive) {
      setViewWindow({ start: 0, end: dataLength - 1 });
      return;
    }
    const size = Math.min(defaultWindow, dataLength);
    setViewWindow({ start: Math.max(0, dataLength - size), end: dataLength - 1 });
  }, [dataLength, isInteractive, defaultWindow]);

  const clampWindow = useCallback((start, end) => {
    const len = dataLength;
    let size = end - start;
    if (size < MIN_WINDOW - 1) size = MIN_WINDOW - 1;
    if (start < 0) { start = 0; end = size; }
    if (end > len - 1) { end = len - 1; start = Math.max(0, end - size); }
    return { start, end };
  }, [dataLength]);

  const handlePan = useCallback((deltaPxX, containerWidth) => {
    const ref = interactionRef.current;
    if (!ref) return;
    const { startWindow } = ref;
    const winSize = startWindow.end - startWindow.start;
    const indexDelta = Math.round((-deltaPxX / containerWidth) * winSize);
    setViewWindow(clampWindow(startWindow.start + indexDelta, startWindow.end + indexDelta));
  }, [clampWindow]);

  const handleZoom = useCallback((scale, baseWindow) => {
    const startSize = baseWindow.end - baseWindow.start;
    let newSize = Math.round(startSize / scale);
    newSize = Math.max(MIN_WINDOW - 1, Math.min(dataLength - 1, newSize));
    const center = (baseWindow.start + baseWindow.end) / 2;
    const newStart = Math.round(center - newSize / 2);
    const newEnd = newStart + newSize;
    setViewWindow(clampWindow(newStart, newEnd));
  }, [clampWindow, dataLength]);

  // RAF ease-out cubic 트윈 (휠 줌처럼 이산적 입력에 부드러운 느낌 부여)
  const animateToWindow = useCallback((target, duration = TWEEN_DURATION) => {
    cancelAnimationFrame(tweenRafRef.current);
    const startWin = { ...viewWindowRef.current };
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const ease = 1 - Math.pow(1 - t, 3);
      const s = Math.round(startWin.start + (target.start - startWin.start) * ease);
      const e = Math.round(startWin.end + (target.end - startWin.end) * ease);
      setViewWindow({ start: s, end: e });
      if (t < 1) tweenRafRef.current = requestAnimationFrame(step);
    };
    tweenRafRef.current = requestAnimationFrame(step);
  }, []);

  // 인터랙션 종료 처리 — pan/pinch/wheel 후에는 sticky 유지, "그냥 탭/클릭"이면 즉시 해제
  const endInteraction = (panned) => {
    interactionRef.current = null;
    if (!panned) setIsInteracting(false);
    // panned=true면 sticky → 다음 깨끗한 탭/클릭 전까지 툴팁 숨김 유지
  };

  const onTouchStart = (e) => {
    cancelAnimationFrame(tweenRafRef.current);
    setIsInteracting(true);
    if (e.touches.length === 1) {
      interactionRef.current = {
        mode: 'pan',
        startX: e.touches[0].clientX,
        startWindow: { ...viewWindowRef.current },
        panned: false,
      };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      interactionRef.current = {
        mode: 'pinch',
        startDist: Math.hypot(dx, dy),
        startWindow: { ...viewWindowRef.current },
        panned: true, // 2손가락은 시작부터 인터랙션 의도
      };
    }
  };

  const onTouchMove = (e) => {
    const ref = interactionRef.current;
    if (!ref || !chartRef.current) return;
    if (e.cancelable) e.preventDefault();
    const rect = chartRef.current.getBoundingClientRect();
    if (ref.mode === 'pan' && e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - ref.startX;
      if (Math.abs(deltaX) > PAN_THRESHOLD_PX) ref.panned = true;
      handlePan(deltaX, rect.width);
    } else if (ref.mode === 'pinch' && e.touches.length === 2) {
      ref.panned = true;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      handleZoom(dist / ref.startDist, ref.startWindow);
    }
  };

  const onTouchEnd = () => {
    endInteraction(interactionRef.current?.panned === true);
  };

  const onMouseDown = (e) => {
    cancelAnimationFrame(tweenRafRef.current);
    setIsInteracting(true);
    interactionRef.current = {
      mode: 'pan',
      startX: e.clientX,
      startWindow: { ...viewWindowRef.current },
      panned: false,
    };
  };

  const onMouseMove = (e) => {
    const ref = interactionRef.current;
    if (!ref || ref.mode !== 'pan' || !chartRef.current) return;
    const delta = e.clientX - ref.startX;
    if (Math.abs(delta) > PAN_THRESHOLD_PX) ref.panned = true;
    const rect = chartRef.current.getBoundingClientRect();
    handlePan(delta, rect.width);
  };

  const onMouseUp = () => {
    endInteraction(interactionRef.current?.panned === true);
  };

  const onWheel = (e) => {
    if (e.cancelable) e.preventDefault();
    const scale = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
    const base = viewWindowRef.current;
    const startSize = base.end - base.start;
    let newSize = Math.round(startSize / scale);
    newSize = Math.max(MIN_WINDOW - 1, Math.min(dataLength - 1, newSize));
    const center = (base.start + base.end) / 2;
    const newStart = Math.round(center - newSize / 2);
    const newEnd = newStart + newSize;
    animateToWindow(clampWindow(newStart, newEnd));
    // 휠도 인터랙션 — sticky 유지. 깨끗한 클릭으로 해제됨
    setIsInteracting(true);
  };

  const bindings = isInteractive
    ? {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        onMouseLeave: onMouseUp,
        onWheel,
      }
    : {};

  return { viewWindow, chartRef, bindings, isInteracting };
};
